
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { 
  Settings, 
  Database, 
  Mail, 
  CreditCard, 
  Users, 
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock
} from "lucide-react";

const AdminSystemControl = () => {
  const { toast } = useToast();
  const [systemSettings, setSystemSettings] = useState({
    maintenanceMode: false,
    newRegistrations: true,
    emailNotifications: true,
    paymentProcessing: true,
    predictionGeneration: true,
    maxDailyPredictions: 100,
    cacheDuration: 3600,
    systemMessage: "",
  });

  const [systemStats, setSystemStats] = useState({
    totalUsers: 1247,
    activeSubscriptions: 456,
    todayPredictions: 2341,
    systemUptime: "99.8%",
    lastBackup: "2 horas atrás",
    databaseSize: "2.3 GB",
  });

  const handleSettingChange = (key: string, value: any) => {
    setSystemSettings(prev => ({
      ...prev,
      [key]: value
    }));
    
    toast({
      title: "Configuração atualizada",
      description: `${key} foi atualizado com sucesso.`,
    });
  };

  const handleSystemAction = (action: string) => {
    switch (action) {
      case "backup":
        toast({
          title: "Backup iniciado",
          description: "O backup do sistema foi iniciado com sucesso.",
        });
        break;
      case "clearCache":
        toast({
          title: "Cache limpo",
          description: "O cache do sistema foi limpo com sucesso.",
        });
        break;
      case "sendNotification":
        toast({
          title: "Notificação enviada",
          description: "Notificação do sistema enviada para todos os usuários.",
        });
        break;
      default:
        break;
    }
  };

  return (
    <div className="space-y-6">
      {/* Status do Sistema */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Status do Sistema
          </CardTitle>
          <CardDescription>
            Monitoramento em tempo real do sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-500">{systemStats.totalUsers}</div>
              <div className="text-sm text-muted-foreground">Usuários Totais</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-500">{systemStats.activeSubscriptions}</div>
              <div className="text-sm text-muted-foreground">Assinantes Ativos</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-500">{systemStats.todayPredictions}</div>
              <div className="text-sm text-muted-foreground">Palpites Hoje</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-500">{systemStats.systemUptime}</div>
              <div className="text-sm text-muted-foreground">Uptime</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium">{systemStats.lastBackup}</div>
              <div className="text-sm text-muted-foreground">Último Backup</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium">{systemStats.databaseSize}</div>
              <div className="text-sm text-muted-foreground">Tamanho do BD</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configurações do Sistema */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configurações Gerais
          </CardTitle>
          <CardDescription>
            Controle as configurações principais do sistema
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Modo Manutenção</Label>
                  <div className="text-sm text-muted-foreground">
                    Bloqueia acesso ao sistema para manutenção
                  </div>
                </div>
                <Switch 
                  checked={systemSettings.maintenanceMode}
                  onCheckedChange={(checked) => handleSettingChange('maintenanceMode', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Novos Cadastros</Label>
                  <div className="text-sm text-muted-foreground">
                    Permite novos usuários se cadastrarem
                  </div>
                </div>
                <Switch 
                  checked={systemSettings.newRegistrations}
                  onCheckedChange={(checked) => handleSettingChange('newRegistrations', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Notificações por Email</Label>
                  <div className="text-sm text-muted-foreground">
                    Envia emails automáticos para usuários
                  </div>
                </div>
                <Switch 
                  checked={systemSettings.emailNotifications}
                  onCheckedChange={(checked) => handleSettingChange('emailNotifications', checked)}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Processamento de Pagamentos</Label>
                  <div className="text-sm text-muted-foreground">
                    Permite novos pagamentos via Stripe
                  </div>
                </div>
                <Switch 
                  checked={systemSettings.paymentProcessing}
                  onCheckedChange={(checked) => handleSettingChange('paymentProcessing', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Geração de Palpites</Label>
                  <div className="text-sm text-muted-foreground">
                    Permite geração de novos palpites
                  </div>
                </div>
                <Switch 
                  checked={systemSettings.predictionGeneration}
                  onCheckedChange={(checked) => handleSettingChange('predictionGeneration', checked)}
                />
              </div>

              <div className="space-y-2">
                <Label>Máximo de Palpites por Dia</Label>
                <Input 
                  type="number"
                  value={systemSettings.maxDailyPredictions}
                  onChange={(e) => handleSettingChange('maxDailyPredictions', parseInt(e.target.value))}
                />
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <Label>Mensagem do Sistema</Label>
            <Input 
              placeholder="Digite uma mensagem para exibir aos usuários..."
              value={systemSettings.systemMessage}
              onChange={(e) => handleSettingChange('systemMessage', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Ações do Sistema */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Ações do Sistema
          </CardTitle>
          <CardDescription>
            Execute ações importantes de manutenção
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              variant="outline" 
              className="h-20 flex flex-col gap-2"
              onClick={() => handleSystemAction('backup')}
            >
              <Database className="w-6 h-6" />
              <span>Fazer Backup</span>
            </Button>
            
            <Button 
              variant="outline" 
              className="h-20 flex flex-col gap-2"
              onClick={() => handleSystemAction('clearCache')}
            >
              <Activity className="w-6 h-6" />
              <span>Limpar Cache</span>
            </Button>
            
            <Button 
              variant="outline" 
              className="h-20 flex flex-col gap-2"
              onClick={() => handleSystemAction('sendNotification')}
            >
              <Mail className="w-6 h-6" />
              <span>Notificar Usuários</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Alertas do Sistema */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Alertas e Monitoramento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <div className="flex-1">
                <div className="font-medium">Sistema funcionando normalmente</div>
                <div className="text-sm text-muted-foreground">Todos os serviços estão operacionais</div>
              </div>
              <Badge variant="secondary">Normal</Badge>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-500" />
              <div className="flex-1">
                <div className="font-medium">Alto uso da CPU</div>
                <div className="text-sm text-muted-foreground">Uso atual: 78% - Monitorar</div>
              </div>
              <Badge variant="outline">Atenção</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSystemControl;
